/**
 * Hydration-safe scroll position composable.
 *
 * On SSR it always returns 0.
 * On client it reads window.scrollY immediately during setup so the first
 * client render reflects the actual scroll position, then keeps listening.
 *
 * Use together with data-allow-mismatch on the element whose classes depend
 * on this value, so Vue patches the class difference gracefully instead of
 * re-rendering the entire subtree.
 */
export function useHydrationSafeScroll() {
  const scrollY = ref(0)
  let animationFrameId: number | null = null

  if (import.meta.env.CLIENT) {
    scrollY.value = window.scrollY || document.documentElement.scrollTop
  }

  const handleScroll = () => {
    scrollY.value = window.scrollY || document.documentElement.scrollTop
  }

  const refreshRestoredScroll = (remainingFrames = 3) => {
    handleScroll()

    if (remainingFrames <= 0) {
      animationFrameId = null
      return
    }

    animationFrameId = window.requestAnimationFrame(() => {
      refreshRestoredScroll(remainingFrames - 1)
    })
  }

  onMounted(() => {
    refreshRestoredScroll()
    window.addEventListener('scroll', handleScroll, { passive: true })
  })

  onBeforeUnmount(() => {
    if (animationFrameId !== null) {
      window.cancelAnimationFrame(animationFrameId)
      animationFrameId = null
    }

    window.removeEventListener('scroll', handleScroll)
  })

  return {
    scrollY,
  }
}
